import { formatSignedPct, formatValue, formatShortDate } from '../../lib/format';
import { useSize } from '../../lib/useSize';
import { useCountUp } from '../../lib/useCountUp';
import { StatusBadge } from '../../ui/Badges';
import { Icon } from '../../ui/Icon';
import type { RegistryEntry, WidgetComponentProps } from '../types';

function Sparkline({ points: all, width, height, wide }: { points: { t: string; v: number | null }[]; width: number; height: number; wide?: boolean }) {
  // Trend of completed days only: today's partial value would always look like a drop.
  const today = new Date().toISOString().slice(0, 10);
  const points = all.length > 3 ? all.filter((p) => p.t !== today) : all;
  const vals = points.map((p) => p.v).filter((v): v is number => v !== null);
  if (vals.length < 2 || width < 20) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = 3;
  const x = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - (max === min ? 0.5 : (v - min) / (max - min)) * (height - pad * 2);
  let d = '';
  let pen = false;
  points.forEach((p, i) => {
    if (p.v === null) {
      pen = false;
      return;
    }
    d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`;
    pen = true;
  });
  const lastIdx = points.map((p) => p.v !== null).lastIndexOf(true);
  const last = points[lastIdx];
  return (
    <svg width={width} height={height} className={wide ? 'kpi-spark-wide' : 'kpi-spark'} aria-hidden>
      <path d={d} fill="none" stroke="var(--axis)" strokeWidth={wide ? 2 : 1.5} strokeLinejoin="round" strokeLinecap="round" />
      {last && last.v !== null && <circle cx={x(lastIdx)} cy={y(last.v)} r={4} fill="var(--accent)" stroke="var(--surface-1)" strokeWidth={2} />}
    </svg>
  );
}

function MetricCard({ data, props }: WidgetComponentProps) {
  const [ref, size] = useSize<HTMLDivElement>();
  const m = data.metric ?? { format: 'text', direction: 'up' };
  const hero = props.variant === 'hero';
  const isText = m.format === 'text';
  const shown = useCountUp(!isText && typeof data.value === 'number' ? data.value : null);
  const value = isText
    ? data.value
    : formatValue(typeof data.value === 'number' ? shown : data.value, m.format, { currency: data.currency, compact: !hero && !props.precise && Math.abs(Number(data.value)) >= 100_000 });
  const cmp = data.comparison;
  let delta = null;
  if (cmp?.state === 'ok' && cmp.delta !== null && cmp.delta !== undefined) {
    const good = (cmp.delta > 0 && m.direction !== 'down') || (cmp.delta < 0 && m.direction === 'down');
    const flat = Math.abs(cmp.delta) < 0.0005;
    delta = (
      <div className="kpi-delta" title={`${formatSignedPct(cmp.delta)} vs ${cmp.label?.toLowerCase()} (${formatValue(cmp.value, m.format, { currency: data.currency })})`}>
        <span className={flat ? 'delta-flat' : good ? 'delta-good' : 'delta-bad'}>
          <span className="sr-only">{cmp.delta > 0 ? 'Up' : cmp.delta < 0 ? 'Down' : 'Flat'} </span>
          <span aria-hidden>{cmp.delta > 0 ? '↑' : cmp.delta < 0 ? '↓' : '→'}</span> {Math.abs(cmp.delta * 100).toFixed(Math.abs(cmp.delta) >= 1 ? 0 : 1)}%
        </span>
        <span className="muted">
          vs {hero ? `${cmp.label?.toLowerCase()} ` : ''}
          {formatValue(cmp.value, m.format, { currency: data.currency, compact: true })}
        </span>
      </div>
    );
  } else if (cmp?.state === 'ok' && cmp.value !== null && cmp.value !== undefined && typeof data.value === 'number') {
    // No percentage when the compared value is zero, but the value itself still helps.
    delta = (
      <div className="kpi-delta" title={`${cmp.label}: ${formatValue(cmp.value, m.format, { currency: data.currency })}`}>
        <span className="muted">vs {formatValue(cmp.value, m.format, { currency: data.currency, compact: true })}</span>
      </div>
    );
  } else if (cmp && cmp.state !== 'ok') {
    delta = (
      <div className="kpi-delta">
        <span className="muted" title={cmp.note}>
          {cmp.state === 'unavailable' ? 'Reported daily' : cmp.state === 'pre-boundary' ? 'Prior period: no data (before sync start)' : 'Prior period partly before sync start'}
        </span>
      </div>
    );
  }
  const bench = props.benchmark;
  const benchStatus = bench && typeof data.value === 'number' ? (data.value < bench.min ? 'below' : data.value > bench.max ? 'above' : 'within') : null;
  const rates = data.citation?.hmrc?.rates ?? [];

  return (
    <div className={`kpi${hero ? ' hero' : ''}`} ref={ref}>
      <div className="kpi-row">
        <div style={{ minWidth: 0 }}>
          <div className={`kpi-value${isText ? ' text' : ''}`} title={typeof data.value === 'number' ? String(data.value) : undefined}>
            {data.error ? <span className="error-text" style={{ fontSize: 14 }}>{data.error}</span> : data.value === null || data.value === undefined ? <span className="muted">No data</span> : value}
          </div>
          {delta}
          {data.sub && <div className="kpi-sub">{data.sub}</div>}
          {data.status && data.status !== 'ok' && <div style={{ marginTop: 6 }}><StatusBadge status={data.status} /></div>}
          {bench && (
            <div className="kpi-sub" style={{ display: 'flex', gap: 5, alignItems: 'center', whiteSpace: 'nowrap' }}>
              {benchStatus && (
                <span style={{ color: benchStatus === 'below' ? 'var(--serious)' : 'var(--good-text)', display: 'inline-flex' }}>
                  <Icon name={benchStatus === 'below' ? 'arrowDown' : 'check'} size={13} label={`${benchStatus} benchmark`} />
                </span>
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {benchStatus ? `${benchStatus[0].toUpperCase()}${benchStatus.slice(1)} ` : ''}benchmark {formatValue(bench.min, m.format, { compact: true })}–{formatValue(bench.max, m.format, { compact: true })}
              </span>
            </div>
          )}
          {data.unsupported && <div className="kpi-sub">{data.unsupported}</div>}
        </div>
        {!hero && props.sparkline && data.sparkline?.length > 1 && size.width > 220 && <Sparkline points={data.sparkline} width={88} height={34} />}
      </div>
      {hero && props.showRateCitation && rates.length > 0 && (
        <div className="rate-cite">
          <span>HMRC monthly rates (units per £1):</span>
          {rates.map((r: any) => (
            <span key={`${r.month}${r.currency}`}>
              <b>{r.currency}</b> {r.unitsPerGbp ?? 'missing'} <span className="muted">({r.month})</span>
            </span>
          ))}
        </div>
      )}
      {hero && props.sparkline && data.sparkline?.length > 1 && (
        <div style={{ marginTop: 'auto', paddingTop: 10 }}>
          <Sparkline points={data.sparkline} width={Math.max(0, size.width)} height={46} wide />
          <div className="muted" style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
            <span>{formatShortDate(data.sparkline[0].t)}</span>
            <span>{formatShortDate(data.sparkline[data.sparkline.length - 1].t)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const metricCard: RegistryEntry = {
  component: MetricCard,
  isEmpty: (d) => d === null || d === undefined,
  toTable: (d) =>
    d?.sparkline?.length
      ? { columns: [{ key: 't', label: 'Day', format: 'date' }, { key: 'v', label: d.metric?.label ?? 'Value', format: d.metric?.format, currency: d.currency, numeric: true }], rows: d.sparkline }
      : null,
};
